import React, { useState } from 'react';
import { IMessage } from './types';
import { AnswerText, chipClass } from './AnswerText';
import {
  CheckIcon,
  ChevronIcon,
  CopyIcon,
  ExternalIcon,
  ShareIcon,
} from '../../utils/icons';
import { SISTANI_ASK_URL } from '../../utils/links';
import { Events, logEvent } from '../../utils/logging/events';

const sourceUrl = (url: string) =>
  url.includes('://') ? url : 'https://' + url;

/** Plain-text version of an answer with its sources, for pasting into a chat. */
const shareText = (question: string | undefined, message: IMessage) => {
  const sources = message.sources ?? [];
  const lines = [
    'AI-generated, so check the sources or ask a scholar. Via marja.ai',
    '',
  ];
  if (question) lines.push(`Q: ${question}`, '');
  lines.push(`A: ${message.content}`);
  if (sources.length) {
    lines.push('', 'Sources:');
    sources.forEach((s, i) => {
      const where = s.url ? sourceUrl(s.url) : s.text.slice(0, 200);
      lines.push(`[${i + 1}] ${s.source ?? ''} ${where}`.replace('  ', ' '));
    });
  }
  return lines.join('\n');
};

const pillClass =
  'inline-flex items-center gap-2 rounded-full border border-line px-4 py-1.5 text-ui text-muted transition-colors hover:border-peach hover:text-peach';

const ShareButtons: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      logEvent(Events.COPY_ANSWER);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked: nothing to confirm
    }
  };

  const share = () => {
    logEvent(Events.SHARE_ANSWER);
    navigator.share({ text }).catch(() => {});
  };

  return (
    <>
      <button type="button" onClick={copy} className={pillClass}>
        {copied ? <CheckIcon /> : <CopyIcon />}
        <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
      </button>
      {canShare && (
        <button type="button" onClick={share} className={pillClass}>
          <ShareIcon /> Share
        </button>
      )}
    </>
  );
};

const SourceCard: React.FC<{
  n: number;
  source: NonNullable<IMessage['sources']>[number];
  targeted: boolean;
}> = ({ n, source, targeted }) => {
  const [expanded, setExpanded] = useState(false);
  const text = (source.text || '').replace(' Answer: ', '\n\nAnswer: ');
  const long = text.length > 280;
  return (
    <li
      id={`src-${n}`}
      className={`!list-none rounded-container border bg-surface px-5 py-4 text-ui transition-colors ${
        targeted ? 'border-peach' : 'border-line'
      }`}
    >
      <div className="mb-2 flex items-center gap-2 text-caption text-muted">
        <span
          className={`${chipClass} ${targeted ? 'bg-peach text-navy' : ''}`}
        >
          {n}
        </span>
        <span>{source.source}</span>
        {source.url && (
          <a
            href={sourceUrl(source.url)}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-peach px-3 py-1 text-peach transition-colors hover:bg-peach hover:text-navy"
          >
            View original <ExternalIcon />
          </a>
        )}
      </div>
      <p
        className={`whitespace-pre-line text-justify hyphens-auto font-normal text-ink/85 ${long && !expanded ? 'line-clamp-4' : ''}`}
      >
        {text}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 text-caption text-peach hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </li>
  );
};

export const MessageView: React.FC<{
  message: IMessage;
  question?: string;
}> = ({ message, question }) => {
  const [open, setOpen] = useState(false);
  const [targeted, setTargeted] = useState<number | null>(null);
  const sources = message.sources ?? [];

  const cite = (n: number) => {
    setOpen(true);
    setTargeted(n);
    window.setTimeout(() => {
      document
        .getElementById(`src-${n}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
    window.setTimeout(() => setTargeted(null), 1800);
  };

  if (message.role === 'user') {
    return (
      <div className="ml-auto max-w-[85%] whitespace-pre-line break-words rounded-container rounded-br-tail bg-bubble-user px-5 py-3 text-left text-body md:max-w-[70%]">
        {message.content}
      </div>
    );
  }

  return (
    <div className="mr-auto flex w-full max-w-[40rem] flex-col gap-4 border-l-2 border-peach-line pl-5 text-left">
      <div className="space-y-4 break-words text-body">
        <AnswerText
          text={message.content}
          onCite={sources.length ? cite : undefined}
        />
      </div>
      {message.no_answer && (
        <a
          href={SISTANI_ASK_URL}
          target="_blank"
          rel="noreferrer"
          onClick={() => logEvent(Events.ASK_OFFICE)}
          className="flex items-center justify-between gap-4 rounded-container border border-peach bg-surface px-5 py-4 text-ui transition-colors hover:border-peach-soft hover:bg-peach/10"
        >
          <span>
            <span className="block text-body">
              Ask His Eminence’s office directly
            </span>
            <span className="block text-muted">
              Send your question to His Eminence’s office on sistani.org.
            </span>
          </span>
          <ExternalIcon />
        </a>
      )}
      {sources.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              className={pillClass}
            >
              {sources.length} {sources.length === 1 ? 'source' : 'sources'}
              <span
                className={`transition-transform ${open ? 'rotate-180' : ''}`}
              >
                <ChevronIcon />
              </span>
            </button>
            {!message.no_answer && (
              <ShareButtons text={shareText(question, message)} />
            )}
          </div>
          {open && (
            <ol className="flex flex-col gap-3 !pl-0">
              {sources.map((s, i) => (
                <SourceCard
                  key={i}
                  n={i + 1}
                  source={s}
                  targeted={targeted === i + 1}
                />
              ))}
            </ol>
          )}
          <p className="text-caption text-muted-soft">
            AI-generated. Check the sources, or{' '}
            <a
              href={SISTANI_ASK_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => logEvent(Events.ASK_OFFICE)}
              className="text-peach underline underline-offset-2 hover:text-peach-soft"
            >
              ask His Eminence’s office directly
            </a>
            .
          </p>
        </>
      )}
    </div>
  );
};
